import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

// Bridge do modo "embed" do player Lit (`slicksell-lit-widget`,
// `embed.html`/`admin-embed-bridge.ts`) — usado por `p-video-player-lit`
// (substitui o antigo Swiper de `p-video-player`) pra embutir o player Lit
// real via iframe (plano registrado em memória
// `plan_lit_player_iframe_embed.md`). Direção oposta do bridge do carrinho
// Nuvemshop (`nube-sdk-bridge.ts`, no widget): aqui O ANGULAR é quem fala
// primeiro — o iframe nunca busca dados sozinho, só recebe `init`/
// `append-items` já prontos (Angular decide fetch/paginação, mesma lógica
// que `p-video-player.component.ts::loadInitialSlides()`/`loadMoreSlides()`
// já usava).
const EMBED_URL = 'https://assets.slicksell.com.br/scripts/codex/embed.html';
const HANDSHAKE_TIMEOUT_MS = 8000;

export interface LitEmbedInitPayload {
  items: any[];
  totalAssets: number;
  storeId: number;
  widgetId: number;
  initialAssetPosition: number;
  sessionId: string;
  playerSessionId: string;
  // Studio (`slick-detail`) — giro 90° inicial. Ausente nas outras 2 vias
  // de embed (`p-player`/`video-modal`), que nunca usam rotação. Mandado
  // aqui (não só em `updateItem()`) pra não depender de um `pushLiveUpdate()`
  // debounced chegar depois pra corrigir a orientação inicial.
  orientation?: 'portrait' | 'landscape';
  // Studio (`slick-detail`) — libera arrastar os hotspots pra reposicionar.
  // Ausente/false nas outras 2 vias de embed (`p-player`/`video-modal`).
  editableHotspots?: boolean;
  // Studio (`slick-detail`) — esconde o X de fechar do player (sem função
  // real no Studio, ver `onCloseRequested` no bridge). Ausente/false nas
  // outras 2 vias de embed.
  hideCloseButton?: boolean;
  // Cores da loja (primária = preenchimento, secundária = hover) usadas
  // pelos botões das sidebars do player Lit. A vitrine real (`slicksell-
  // widget.ts`) já recebe isso via `assets_public`; as 3 vias de embed
  // (Studio/`slick-detail`, `video-modal`, `p-player`) nunca mandavam esse
  // campo pro iframe, então o player caía sempre no fallback hardcoded do
  // Lit widget (bug real corrigido, 2026-08-28).
  storeColors?: { primary_color?: string | null; secondary_color?: string | null };
}

export interface LitEmbedAppendPayload {
  items: any[];
  totalAssets: number;
}

// Modo embed live-edit (Studio, `slick-detail`) — reescreve o item JÁ
// montado no player (sem re-`init`). `item` é o mesmo `SlicksellAssetItem`
// bruto usado em `items[]` de `LitEmbedInitPayload` (não um formato
// próprio) — o lado Lit normaliza com a MESMA função (`extractPlayerItems`)
// que `init`/`append-items` já usam.
export interface LitEmbedUpdateItemPayload {
  item: any;
  orientation?: 'portrait' | 'landscape';
  editableHotspots?: boolean;
  hideCloseButton?: boolean;
  storeColors?: { primary_color?: string | null; secondary_color?: string | null };
}

export interface LitEmbedTrackEventDetail {
  type: string;
  details: string;
  ctaId: number | string;
  uid?: string;
}

export interface LitEmbedHotspotPositionChangeDetail {
  sku: string;
  xPct: number;
  yPct: number;
}

export class LitPlayerEmbedHandle {
  onRequestMore?: () => void;
  // Vem do X/Escape do próprio player Lit (`ss-player-close`, repassado
  // pelo iframe via `slicksell:embed:close`) — sem isso, só o X externo do
  // componente pai (ex. `video-modal`) conseguia fechar de verdade.
  onCloseRequested?: () => void;
  // Vem de `ss-track-event` (`slicksell-player.ts::trackEvent()`), relayed
  // via `slicksell:embed:track-event` — usado hoje só pelo Studio pra
  // escutar o evento de "pitch" (tempo até o pitch) que o player já
  // dispara sozinho, em vez de reimplementar o watch de timestamp.
  onTrackEvent?: (detail: LitEmbedTrackEventDetail) => void;
  // Vem de `ss-hotspot-position-change` (`slicksell-hotspot-layer.ts`,
  // soltou um arrasto — só existe quando `editableHotspots`, Studio).
  onHotspotPositionChange?: (detail: LitEmbedHotspotPositionChangeDetail) => void;

  private ready: Promise<void>;
  private resolveReady!: () => void;
  private handshakeTimer: any;
  private destroyed = false;
  private messageListener = (event: MessageEvent) => {
    if (event.source !== this.iframe.contentWindow) return;
    if (event.origin !== this.embedOrigin) return;

    const msg = event.data;
    if (msg?.type === 'slicksell:embed:ready') {
      this.resolveReady();
    } else if (msg?.type === 'slicksell:embed:request-more') {
      this.onRequestMore?.();
    } else if (msg?.type === 'slicksell:embed:close') {
      this.onCloseRequested?.();
    } else if (msg?.type === 'slicksell:embed:track-event') {
      this.onTrackEvent?.(msg.data);
    } else if (msg?.type === 'slicksell:embed:hotspot-position-change') {
      this.onHotspotPositionChange?.(msg.data);
    }
  };

  constructor(private iframe: HTMLIFrameElement, private embedOrigin: string) {
    this.ready = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
      this.handshakeTimer = setTimeout(resolve, HANDSHAKE_TIMEOUT_MS);
    });
    window.addEventListener('message', this.messageListener);
  }

  async init(payload: LitEmbedInitPayload): Promise<void> {
    await this.ready;
    if (this.destroyed) return;
    this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:init', data: payload }, this.embedOrigin);
  }

  async appendItems(payload: LitEmbedAppendPayload): Promise<void> {
    await this.ready;
    if (this.destroyed) return;
    this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:append-items', data: payload }, this.embedOrigin);
  }

  // Modo embed live-edit (Studio) — mesmo padrão de `init`/`appendItems`,
  // sem retorno/correlação: o pai (slick-detail) chama isso a cada edição
  // de CTA/cor/capítulo/rotação, tipicamente já debounced do lado de quem
  // chama (ver `pushLiveUpdate()` em `slick-detail.component.ts`).
  async updateItem(payload: LitEmbedUpdateItemPayload): Promise<void> {
    await this.ready;
    if (this.destroyed) return;
    this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:update-item', data: payload }, this.embedOrigin);
  }

  // Modo embed live-edit (Studio) — único caso de request/response do bridge
  // (todo o resto é push unidirecional). Usado pra captura de thumbnail
  // ("Salvar imagem deste momento"): o instante de reprodução agora só
  // existe DENTRO do iframe, não tem mais `<video>` no lado Angular pra ler
  // `currentTime` direto. Timeout curto de segurança — se o handshake nunca
  // respondeu (iframe travado/fechado no meio do caminho), resolve 0 em vez
  // de travar quem chamou pra sempre.
  async getCurrentTime(): Promise<number> {
    await this.ready;
    if (this.destroyed) return 0;

    return new Promise<number>((resolve) => {
      const requestId = Math.random().toString(36).slice(2);
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', listener);
        resolve(0);
      }, 3000);
      const listener = (event: MessageEvent) => {
        if (event.source !== this.iframe.contentWindow) return;
        if (event.origin !== this.embedOrigin) return;
        const msg = event.data;
        if (msg?.type === 'slicksell:embed:current-time-response' && msg.data?.requestId === requestId) {
          clearTimeout(timeoutId);
          window.removeEventListener('message', listener);
          resolve(Number(msg.data.time) || 0);
        }
      };
      window.addEventListener('message', listener);
      this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:request-current-time', data: { requestId } }, this.embedOrigin);
    });
  }

  // Vem do fechamento (X do player ou X externo de quem criou o iframe,
  // ex. `video-modal.component.ts::closeModal()`) — o iframe agora fica
  // vivo entre aberturas (ver `LitPlayerEmbedBridgeService`/plano em
  // memória), então precisa avisar o lado Lit pra derrubar o
  // `<slicksell-player>` NA HORA, em vez de deixar rodando escondido até a
  // próxima abertura mandar um novo `init`. Fire-and-forget — se o
  // handshake ainda não terminou, não há player montado pra derrubar mesmo.
  // Modo embed live-edit (Studio) — pausa o vídeo atual (sem retomar).
  // Fire-and-forget, mesmo padrão de `updateItem()`. Usado por
  // `onThumbnailModeChange()` ao trocar pro modo "Selecionar imagem do
  // vídeo" — o instante de reprodução só existe dentro do iframe, não tem
  // mais `<video>` no lado Angular pra chamar `.pause()` direto.
  async pause(): Promise<void> {
    await this.ready;
    if (this.destroyed) return;
    this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:pause' }, this.embedOrigin);
  }

  async teardown(): Promise<void> {
    await this.ready;
    if (this.destroyed) return;
    this.iframe.contentWindow?.postMessage({ type: 'slicksell:embed:teardown' }, this.embedOrigin);
  }

  // Bug real corrigido (suspeita de acúmulo de memória/lentidão entre
  // aberturas seguidas): `iframe.remove()` sozinho não força o navegador a
  // descartar o documento antigo (com o player Lit/keen-slider/vídeos
  // decodificados) imediatamente — o realm só é liberado de verdade quando
  // o iframe NAVEGA pra outro documento ou é coletado pelo GC, o que pode
  // demorar. Navegar pra `about:blank` ANTES de remover força o unload
  // síncrono do documento antigo, soltando os decoders de vídeo mais cedo.
  // `handshakeTimer` também precisa ser limpo — sem isso, um `destroy()`
  // chamado antes do handshake responder deixava o timer vivo por até 8s,
  // segurando esta instância inteira (e a cadeia `iframe`→`contentWindow`)
  // via closure do `setTimeout`.
  destroy(): void {
    this.destroyed = true;
    clearTimeout(this.handshakeTimer);
    window.removeEventListener('message', this.messageListener);
    this.onRequestMore = undefined;
    this.onCloseRequested = undefined;
    this.onTrackEvent = undefined;
    this.onHotspotPositionChange = undefined;
    try {
      this.iframe.src = 'about:blank';
    } catch {
      // Ignorar — navegação pro blank é só uma dica de limpeza, não crítica.
    }
    this.iframe.remove();
  }
}

@Injectable({ providedIn: 'root' })
export class LitPlayerEmbedBridgeService {
  createIframe(container: HTMLElement): LitPlayerEmbedHandle {
    const embedOrigin = new URL(EMBED_URL).origin;
    const parentOrigin = window.location.origin;

    // `environment.serverUrl` já vem com sufixo `/api` (ex.:
    // "https://stage-api.slicksell.com.br/api") — `api.ts`/`chat-api.ts`
    // (slicksell-lit-widget) montam suas próprias rotas com `/api/...` na
    // frente, então tiramos o sufixo aqui. TEMPORÁRIO: enquanto o pipeline
    // de stage do slicksell-lit-widget não separa prod/stage de verdade,
    // isso garante que testar em stage-app bate em stage-api — reverte
    // sozinho quando `environment.production` for `true` (prod nunca manda
    // esse parâmetro, `api.ts` já usa prod por padrão).
    const apiBase = environment.production ? '' : environment.serverUrl.replace(/\/api\/?$/, '');

    const iframe = document.createElement('iframe');
    const apiBaseParam = apiBase ? `&apiBase=${encodeURIComponent(apiBase)}` : '';
    iframe.src = `${EMBED_URL}?parentOrigin=${encodeURIComponent(parentOrigin)}${apiBaseParam}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    container.appendChild(iframe);

    return new LitPlayerEmbedHandle(iframe, embedOrigin);
  }
}
